import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Copy, PartyPopper, Clock, IndianRupee, FileDown } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const stepVariants = {
  enter: { opacity: 0, x: 40 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -40 },
};

const inputCls =
  "h-14 w-full rounded-xl border-2 border-slate-200 bg-white px-4 text-lg font-semibold text-slate-800 outline-none transition-colors focus:border-sky-400 placeholder:font-medium placeholder:text-slate-400";

export const CheckoutModal = ({ product, open, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [order, setOrder] = useState(null);
  const [upiId, setUpiId] = useState("");
  const [txnRef, setTxnRef] = useState("");

  const close = () => {
    onClose();
    setTimeout(() => {
      setStep(1);
      setOrder(null);
      setUpiId("");
      setTxnRef("");
      setForm({ name: "", email: "", phone: "" });
    }, 300);
  };

  const createOrder = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/orders`, {
        ...form,
        product_slug: product.slug,
      });
      setOrder(data);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start your order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const goToReference = (e) => {
    e.preventDefault();
    if (!upiId.includes("@")) {
      toast.error("Please enter a valid UPI ID (e.g. yourname@okhdfcbank)");
      return;
    }
    setStep(3);
  };

  const confirmPayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/orders/${order.order_id}/confirm`, {
        provider: "manual_upi",
        upi_id: upiId,
        txn_ref: txnRef,
      });
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not confirm payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const copyUpi = () => {
    navigator.clipboard.writeText(order.payment.merchant_upi_id);
    toast.success("UPI ID copied!");
  };

  if (!product) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent
        data-testid="checkout-modal"
        className="max-h-[92vh] max-w-lg overflow-y-auto rounded-[2rem] border-4 border-sky-100 bg-white p-6 sm:p-8"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-slate-800">
            {step === 1 && "Checkout"}
            {step === 2 && "Pay with UPI"}
            {step === 3 && "Confirm payment"}
            {step === 4 && "Payment submitted!"}
          </DialogTitle>
        </DialogHeader>

        <div className="mb-5 flex items-center gap-2" data-testid="checkout-steps">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`h-2 flex-1 rounded-full transition-colors ${
                s <= step ? "bg-brand-green" : "bg-slate-100"
              }`}
            />
          ))}
        </div>

        <div className="mb-4 flex items-center justify-between rounded-2xl bg-sky-soft px-5 py-4">
          <div>
            <p className="font-display text-lg font-bold text-slate-800">{product.name}</p>
            <p className="text-sm font-semibold text-slate-500">{product.tagline}</p>
          </div>
          <p className="flex items-center font-display text-2xl font-black text-slate-800">
            <IndianRupee className="h-5 w-5" />
            {product.price}
          </p>
        </div>

        <p
          data-testid="digital-product-notice"
          className="mb-5 flex items-start gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700"
        >
          <FileDown className="mt-0.5 h-4 w-4 shrink-0" />
          This is a digital PDF product. No physical item will be shipped.
        </p>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.form
              key="step1"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25 }}
              onSubmit={createOrder}
              className="space-y-4"
            >
              <input
                data-testid="checkout-name-input"
                required
                minLength={2}
                placeholder="Your name"
                className={inputCls}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <input
                data-testid="checkout-email-input"
                required
                type="email"
                placeholder="Email address"
                className={inputCls}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <input
                data-testid="checkout-phone-input"
                type="tel"
                maxLength={10}
                pattern="[0-9]{10}"
                placeholder="Phone (optional)"
                className={inputCls}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
              <button
                data-testid="checkout-continue-button"
                type="submit"
                disabled={loading}
                className="h-14 w-full rounded-full bg-brand-blue font-display text-xl font-bold text-white shadow-[0_5px_0_0_#1D4ED8] transition-transform active:translate-y-1 active:shadow-none disabled:opacity-60"
              >
                {loading ? <Loader2 className="mx-auto h-6 w-6 animate-spin" /> : "Continue to Payment"}
              </button>
            </motion.form>
          )}

          {step === 2 && order && (
            <motion.form
              key="step2"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25 }}
              onSubmit={goToReference}
              className="space-y-4"
            >
              <div
                data-testid="manual-upi-notice"
                className="rounded-xl bg-sky-50 px-4 py-3 text-sm font-semibold text-slate-600"
              >
                <p className="font-display font-bold text-slate-700">
                  This is a manual UPI payment process.
                </p>
                <p className="mt-1">
                  Your download will remain locked until payment is verified. Once the store owner
                  verifies your transfer, your download unlocks and the link is emailed to you.
                </p>
              </div>
              <div className="rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50 p-5 text-center">
                <p className="text-sm font-bold uppercase tracking-wide text-amber-600">
                  Pay ₹{order.payment.amount} to this UPI ID
                </p>
                <div className="mt-2 flex items-center justify-center gap-2">
                  <span
                    data-testid="merchant-upi-id"
                    className="font-display text-xl font-bold text-slate-800"
                  >
                    {order.payment.merchant_upi_id}
                  </span>
                  <button
                    data-testid="copy-upi-button"
                    type="button"
                    onClick={copyUpi}
                    className="rounded-full bg-white p-2 text-slate-500 shadow-sm transition-colors hover:text-brand-blue"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-2 text-sm font-semibold text-slate-500">
                  Use GPay, PhonePe, Paytm or any UPI app
                </p>
              </div>
              <input
                data-testid="checkout-upi-id-input"
                required
                placeholder="Your UPI ID (e.g. name@okhdfcbank)"
                className={inputCls}
                value={upiId}
                onChange={(e) => setUpiId(e.target.value)}
              />
              <button
                data-testid="i-have-paid-button"
                type="submit"
                className="h-14 w-full rounded-full bg-brand-green font-display text-xl font-bold text-white shadow-[0_5px_0_0_#15803D] transition-transform active:translate-y-1 active:shadow-none"
              >
                I Have Paid
              </button>
            </motion.form>
          )}

          {step === 3 && (
            <motion.form
              key="step3"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25 }}
              onSubmit={confirmPayment}
              className="space-y-4"
            >
              <p className="text-base font-semibold text-slate-600">
                Almost done! Enter the UPI transaction reference number (UTR) from your payment
                app so we can confirm your order.
              </p>
              <input
                data-testid="txn-ref-input"
                required
                minLength={6}
                placeholder="Transaction reference / UTR number"
                className={inputCls}
                value={txnRef}
                onChange={(e) => setTxnRef(e.target.value)}
              />
              <button
                data-testid="confirm-payment-button"
                type="submit"
                disabled={loading}
                className="h-14 w-full rounded-full bg-brand-red font-display text-xl font-bold text-white shadow-[0_5px_0_0_#B91C1C] transition-transform active:translate-y-1 active:shadow-none disabled:opacity-60"
              >
                {loading ? <Loader2 className="mx-auto h-6 w-6 animate-spin" /> : "Confirm Payment"}
              </button>
            </motion.form>
          )}

          {step === 4 && (
            <motion.div
              key="step4"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25 }}
              className="space-y-5 text-center"
              data-testid="payment-success"
            >
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-amber-100">
                <Clock className="h-10 w-10 text-brand-yellow" />
              </div>
              <p className="text-lg font-bold text-slate-700">
                Thank you, {form.name.split(" ")[0]}! Your payment reference has been received.
              </p>
              <p className="text-base font-semibold text-slate-500">
                We're verifying your payment. Once verified, your download link will be emailed to{" "}
                <span className="font-bold text-slate-700">{form.email}</span> and unlocked on your
                order page.
              </p>
              <button
                data-testid="go-to-download-button"
                onClick={() => navigate(`/thank-you/${order.order_id}`)}
                className="h-14 w-full rounded-full bg-brand-purple font-display text-xl font-bold text-white shadow-[0_5px_0_0_#7E22CE] transition-transform active:translate-y-1 active:shadow-none"
              >
                <span className="inline-flex items-center gap-2">
                  View My Order <PartyPopper className="h-5 w-5" />
                </span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
